import Page from "../Page";
import { useAccess } from "src/hooks/useAccess.hook";
import { Card, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BaseTable } from "../BaseTable/BaseTable";
import { useList } from "src/hooks/useFetch.hook";
import { roleListColumns } from "./roles.constants";
import { FEATURE } from "src/util/enums";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Stack } from "@mui/material";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    boxShadow: theme.customShadows.z8,
  }
}));

export const RoleList = () => {
  const navigate = useNavigate()
  const hasCreateAccess = useAccess(FEATURE.ROLES, "C");
  const isReadOnly = useAccess(FEATURE.ROLES, "R");
  const { data: roles, isFetching } = useList("roles");
  const [tableData, setTableData] = useState([]);
  const rowOrCellClickRedirect = ({ id }) => {
    if(isReadOnly) return;
    navigate(`/roles/${id}`);
  };

  useEffect(() => {
    if (!isFetching && roles){
      let data = Array.isArray(roles) ? [...roles] : [];
      setTableData(data);
    }
  }, [isFetching, roles]);

  const handleSearching = (event) => {
    const dataToSearchFrom = Array.isArray(roles) ? roles : [];
    const searchValue = event.target.value.trim().toString().toLowerCase();
    if (searchValue) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "roleName",
            "createTime",
            "createdBy",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "createTime" || key === "createdBy"
                ? yearFirstDateTimeFormat(value).toString()
                : value ? value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchValue)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData(Array.isArray(roles) ? [...roles] : []);
    }
  };
  return (
    <Page
      {...(hasCreateAccess && { path: `/roles/new` })}
      actionButtonText="Add Role"
    >
      {/* Search Section */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <CommonSearchbar
            placeholder="Search roles"
            searchFunc={(e) => handleSearching(e)}
          />
        </Stack>
      </Card>

      {/* Table Section */}
      <StyledListCard>
        <BaseTable
          rows={tableData ?? []}
          columns={roleListColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row.roleId}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={10}
          isReadOnly={isReadOnly}
          showExportCsvBtn={false}
          height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              height: '52px !important',
            },
            '& .MuiDataGrid-cell': {
              height: '52px !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.primary.lighter,
              borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
            },
            '& .MuiDataGrid-columnHeader': {
              fontWeight: 600,
              color: (theme) => theme.palette.primary.main,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
              }
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: (theme) => theme.palette.action.hover,
            }
          }}
        />
      </StyledListCard>
    </Page>
  );
};
